import { type GanttView, type PlanDraftItemView, type PlanDraftView } from '@apm/contracts';

/**
 * Đổi kế hoạch nháp thành dữ liệu mà `<GanttChart>` của `APM-503` vẽ được.
 *
 * Dùng lại nguyên component thay vì viết một Gantt thứ hai cho màn hình review:
 * hai Gantt thì hai lần sửa cho mỗi thay đổi, và bản xem trước sẽ dần trông
 * khác bản thật — đúng lúc người dùng cần chúng giống nhau để tin vào nó.
 *
 * Nháp lưu lịch **tương đối** (`startOffsetDays`), nên hàm này cần ngày khởi
 * động dự kiến để dựng ngày tuyệt đối. Đổi ngày đó thì cả biểu đồ trượt theo —
 * và đó chính là thứ người dùng muốn thấy trước khi bấm "Áp dụng".
 */
export function draftToGantt(draft: PlanDraftView, startDate: Date): GanttView {
  const topLevel = draft.items.filter((item) => item.parentId === null && !item.excluded);
  const idToPosition = new Map(topLevel.map((item, index) => [item.id, index]));

  const bars = topLevel.map((item) => {
    const range = datesOf(item, startDate);

    return {
      task: {
        id: item.id,
        projectId: draft.projectId,
        key: `#${(idToPosition.get(item.id) ?? 0) + 1}`,
        number: (idToPosition.get(item.id) ?? 0) + 1,
        title: item.title,
        description: item.description,
        // Nháp chưa phân biệt mốc — `GanttBar.kind` ngay dưới cũng đặt `task`.
        kind: 'task' as const,
        taskTypeId: item.id,
        taskTypeName: item.taskTypeSlug ?? '',
        // Nháp chưa gắn loại task thật nên chưa có màu — xem `TaskTypeTag`.
        taskTypeColor: null,
        groupId: null,
        statusId: item.id,
        statusName: '',
        statusCategory: 'todo' as const,
        parentId: null,
        sprintId: null,
        assigneeId: item.suggestedAssigneeId,
        assigneeName: item.suggestedAssigneeName,
        /* Việc trong bản nháp **chưa tồn tại**, nên chưa ai tạo nó cả. `null` =
           chưa biết, không phải "không do PM tạo" — xem `TaskView.createdByPm`. */
        createdById: null,
        createdByName: null,
        createdByPm: null,
        priority: 'medium' as const,
        estimateHours: item.estimateHours,
        // Việc mới trong bản nháp chưa từng chạy, nên chưa có số thực tế nào.
        actualStartAt: null,
        actualEndAt: null,
        actualHours: null,
        trackedHours: null,
        storyPoints: null,
        progressPct: 0,
        startDate: range.startDate,
        dueDate: range.dueDate,
        isBlocked: false,
        blockReason: null,
        position: item.position,
        subtaskCount: draft.items.filter((child) => child.parentId === item.id).length,
        createdAt: draft.createdAt,
      },
      kind: 'task' as const,
      startDate: range.startDate,
      dueDate: range.dueDate,
      baselineStartDate: null,
      baselineDueDate: null,
      // Đường găng do **server** tính, bằng đúng `computeCriticalPath` mà Gantt
      // thật dùng (APM-1314). Trước đây chỗ này để `false` vì nháp chưa qua CPM
      // — và hệ quả là người duyệt không thấy được chuỗi việc quyết định ngày
      // về đích cho tới **sau** khi đã bấm Áp dụng.
      critical: item.critical,
      totalFloat: item.totalFloat,
      violationDays: 0,
      earliestStartDate: range.startDate,
      slippageDays: null,
    };
  });

  const criticalIds = new Set(topLevel.filter((item) => item.critical).map((item) => item.id));

  const links = topLevel.flatMap((item) =>
    item.dependsOn
      .filter((from) => idToPosition.has(from))
      .map((from) => ({
        id: `${from}-${item.id}`,
        fromTaskId: from,
        toTaskId: item.id,
        type: 'finish_to_start' as const,
        lagDays: 0,
        // Cạnh găng khi **cả hai đầu** đều găng — cùng luật với `GanttService`.
        critical: criticalIds.has(from) && criticalIds.has(item.id),
      })),
  );

  const dated = bars.map((bar) => bar.startDate).filter((date): date is string => date !== null);
  const ends = bars.map((bar) => bar.dueDate).filter((date): date is string => date !== null);

  return {
    projectId: draft.projectId,
    windowStart: dated.length > 0 ? dated.reduce((a, b) => (a < b ? a : b)) : null,
    windowEnd: ends.length > 0 ? ends.reduce((a, b) => (a > b ? a : b)) : null,
    baselineSetAt: null,
    bars,
    links,
    // Nhóm việc không tồn tại ở ngữ cảnh này (nháp / tua lại).
    groups: [],
    criticalPathDays: draft.criticalPathDays,
  };
}

/**
 * Phép **đảo ngược** của `datesOf` — APM-ROLE-w.
 *
 * Kéo một thanh trên Gantt của bản nháp cho ra hai ngày tuyệt đối, nhưng nháp
 * lưu lịch **tương đối** so với ngày khởi động dự kiến. Hàm này quy đổi ngược,
 * và nó là hàm thuần vì đây là chỗ một lỗi lệch-một-ngày sống sót lâu nhất:
 * biểu đồ vẫn vẽ, thanh vẫn đúng chỗ vừa thả, chỉ là con số ghi xuống lệch.
 *
 * ## Vì sao chặn dưới thay vì báo lỗi
 *
 * `startOffsetDays` không âm được (`UpdatePlanItemInput` ép `min(0)`), và kéo
 * một việc về **trước** ngày dự án khởi động là vô nghĩa: muốn cả kế hoạch chạy
 * sớm hơn thì đổi chính ngày khởi động. Chặn ở 0 làm thanh bật lại về mốc đầu
 * — người dùng thấy ngay là không thả được ở đó, không phải im lặng nuốt.
 */
export function rangeToOffsets(
  range: { startDate: string; dueDate: string },
  planStart: Date,
): { startOffsetDays: number; durationDays: number } {
  const start = Date.parse(`${range.startDate}T00:00:00Z`);
  const due = Date.parse(`${range.dueDate}T00:00:00Z`);

  const offset = Math.round((start - planStart.getTime()) / 86_400_000);
  // `[start, due]` là khoảng **đóng**, khớp `datesOf`: dài 1 ngày ⇒ hai mốc trùng.
  const duration = Math.round((due - start) / 86_400_000) + 1;

  return {
    startOffsetDays: Math.max(0, offset),
    durationDays: Math.max(1, duration),
  };
}

function datesOf(
  item: PlanDraftItemView,
  startDate: Date,
): { startDate: string | null; dueDate: string | null } {
  if (item.startOffsetDays === null || item.durationDays === null) {
    return { startDate: null, dueDate: null };
  }

  const start = new Date(startDate.getTime() + item.startOffsetDays * 86_400_000);
  // `[start, due]` là khoảng **đóng**, khớp APM-503: dài 1 ngày ⇒ hai mốc trùng.
  const due = new Date(start.getTime() + (item.durationDays - 1) * 86_400_000);

  return { startDate: iso(start), dueDate: iso(due) };
}

const iso = (date: Date): string => date.toISOString().slice(0, 10);

/** Tổng giờ của một hạng mục: của chính nó, hoặc cộng từ các con nếu đã chia nhỏ. */
export function rolledUpHours(draft: PlanDraftView, item: PlanDraftItemView): number | null {
  const children = draft.items.filter((child) => child.parentId === item.id);
  if (children.length === 0) return item.estimateHours;

  // Đã chia nhỏ thì con là nguồn sự thật — cùng luật roll-up với task thật
  // (spec 9.1), để con số ở nháp và con số sau khi áp dụng không lệch nhau.
  return children.reduce((sum, child) => sum + (child.estimateHours ?? 0), 0);
}
